import React, { useEffect, useState, useRef } from 'react'
import {Upload, X, ExternalLink, BarChart, Eye, MousePointer, CreditCard, Star, Clock} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useProjects, Project } from '../hooks/useProjects'
import { useFileUpload } from '../hooks/useFileUpload'
import { lumi } from '../lib/lumi'
import { BannerUploadModal } from './BannerUploadModal'
import { ProfileEditModal } from './ProfileEditModal'

export function Dashboard() {
  const { user, signOut } = useAuth()
  const { projects, loading, fetchUserProjects, createProject, updateProject, deleteProject } = useProjects()
  const { uploadSingleFile, uploading } = useFileUpload()
  
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [projectName, setProjectName] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string>('')
  const [processingPayment, setProcessingPayment] = useState(false)
  const [selectedProjectForBanner, setSelectedProjectForBanner] = useState<Project | null>(null)
  const [selectedProjectForProfile, setSelectedProjectForProfile] = useState<Project | null>(null)
  const [showFeaturedModal, setShowFeaturedModal] = useState(false)
  const [selectedProjectForFeatured, setSelectedProjectForFeatured] = useState<Project | null>(null)
  const [selectedTier, setSelectedTier] = useState<string | null>(null)
  const [processingFeatured, setProcessingFeatured] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Check for payment session on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const sessionId = urlParams.get('session_id')
    const cancelled = urlParams.get('cancelled')

    if (sessionId) {
      // Payment successful, poll for project creation
      checkPaymentStatus(sessionId)
    } else if (cancelled) {
      alert('Payment was cancelled. You can try again anytime.')
      // Clean URL
      window.history.replaceState({}, '', '/dashboard')
    }
  }, [])

  const checkPaymentStatus = async (sessionId: string) => {
    setProcessingPayment(true)
    let attempts = 0
    const maxAttempts = 15 // 30 seconds max

    const poll = async () => {
      attempts++
      
      try {
        // Refresh projects list to check if new project appeared
        if (user) {
          await fetchUserProjects(user.userId)
          
          // Check if we have any active projects (webhook processed payment)
          const hasActiveProject = projects.some(p => p.subscriptionStatus === 'active')
          
          if (hasActiveProject) {
            setProcessingPayment(false)
            alert('Payment successful! Your project is now live.')
            window.history.replaceState({}, '', '/dashboard')
            return
          }
        }

        if (attempts < maxAttempts) {
          setTimeout(poll, 2000) // Poll every 2 seconds
        } else {
          setProcessingPayment(false)
          alert('Payment received! Your project will be activated shortly.')
          window.history.replaceState({}, '', '/dashboard')
        }
      } catch (error) {
        console.error('Poll error:', error)
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000)
        } else {
          setProcessingPayment(false)
          window.history.replaceState({}, '', '/dashboard')
        }
      }
    }

    poll()
  }

  useEffect(() => {
    if (user) {
      fetchUserProjects(user.userId)
    }
  }, [user])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB')
        return
      }
      setSelectedFile(file)
      setPreviewUrl(URL.createObjectURL(file))
    }
  }

  const handleStartPayment = async () => {
    if (!projectName || !websiteUrl || !user) {
      alert('Please fill in project name and website URL')
      return
    }

    try {
      setProcessingPayment(true)

      // Call Deno function to create checkout session
      const result = await lumi.functions.invoke('createCheckoutSession', {
        method: 'POST',
        body: {
          projectName,
          websiteUrl
        }
      })

      // Open Stripe checkout in new tab
      if (result?.url) {
        const win = window.open()
        if (win) {
          win.location.href = result.url
        } else {
          // Fallback if popup blocked
          window.location.href = result.url
        }
        
        // Close modal
        setShowUploadModal(false)
        setProjectName('')
        setWebsiteUrl('')
        setSelectedFile(null)
        setPreviewUrl('')
      }
    } catch (error: any) {
      console.error('Payment error:', error)
      const errorMessage = error?.data?.error || 'Failed to start payment. Please try again.'
      alert(errorMessage)
    } finally {
      setProcessingPayment(false)
    }
  }

  const handleDeleteProject = async (projectId: string) => {
    if (confirm('Are you sure you want to delete this project?')) {
      try {
        await deleteProject(projectId)
        alert('Project deleted successfully')
      } catch (error) {
        console.error('Delete error:', error)
        alert('Failed to delete project')
      }
    }
  }

  const handlePurchaseFeatured = async (tier: string) => {
    if (!selectedProjectForFeatured || !user) return

    setProcessingFeatured(true)
    try {
      const result = await lumi.functions.invoke('purchaseFeaturedListing', {
        method: 'POST',
        body: {
          projectId: selectedProjectForFeatured._id,
          tier
        }
      })

      if (result?.url) {
        const win = window.open()
        if (win) {
          win.location.href = result.url
        } else {
          window.location.href = result.url
        }
        setShowFeaturedModal(false)
        setSelectedProjectForFeatured(null)
        setSelectedTier(null)
      }
    } catch (error: any) {
      console.error('Featured purchase error:', error)
      alert(error?.data?.error || 'Failed to purchase featured listing')
    } finally {
      setProcessingFeatured(false)
    }
  }

  const getRemainingDays = (featuredUntil?: string) => {
    if (!featuredUntil) return 0
    const now = new Date()
    const until = new Date(featuredUntil)
    const days = Math.ceil((until.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    return Math.max(0, days)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0A0E27]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <img 
                src="https://static.lumi.new/44/44423e965e784fa5d04b63cff5203ecd.webp" 
                alt="Base Builders Logo" 
                className="w-10 h-10 rounded-lg object-cover"
              />
              <h1 className="text-2xl font-bold text-white">Base Builders</h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-gray-300">Welcome, {user?.userName}</span>
              <button
                onClick={signOut}
                className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Dashboard Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-4xl font-bold text-white mb-2">My Projects</h2>
            <p className="text-gray-400">Manage your listed projects and subscriptions</p>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            <Upload className="w-5 h-5" />
            Add New Project
          </button>
        </div>

        {/* Projects Grid */}
        {projects.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center">
              <Upload className="w-10 h-10 text-gray-500" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">No Projects Yet</h3>
            <p className="text-gray-400 mb-6">Create your first project to start showcasing your NFT or crypto venture</p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              Create Project
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {projects.map((project) => (
              <div key={project._id} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                {/* Banner Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.bannerImageUrl}
                    alt={project.projectName}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3 flex gap-2">
                    {project.isFeatured && project.featuredUntil && new Date(project.featuredUntil) > new Date() && (
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black flex items-center gap-1">
                        <Star className="w-3 h-3" />
                        Featured
                      </span>
                    )}
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      project.subscriptionStatus === 'active'
                        ? 'bg-green-500/80 text-white'
                        : 'bg-yellow-500/80 text-white'
                    }`}>
                      {project.subscriptionStatus}
                    </span>
                  </div>
                </div>

                {/* Project Info */}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">{project.projectName}</h3>
                  <a
                    href={project.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[#60A5FA] hover:text-[#0052FF] transition-colors mb-4"
                  >
                    {project.websiteUrl}
                    <ExternalLink className="w-4 h-4" />
                  </a>

                  {/* Featured Countdown */}
                  {project.isFeatured && project.featuredUntil && new Date(project.featuredUntil) > new Date() && (
                    <div className="mb-3 bg-gradient-to-r from-[#FFD700]/10 to-[#FFA500]/10 border border-[#FFD700]/30 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-[#FFD700]">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          Featured expires in {getRemainingDays(project.featuredUntil)} days
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-white/5 p-3 rounded-lg">
                      <div className="flex items-center gap-2 text-gray-400 mb-1">
                        <Eye className="w-4 h-4" />
                        <span className="text-sm">Views</span>
                      </div>
                      <span className="text-2xl font-bold text-white">{project.viewCount}</span>
                    </div>
                    <div className="bg-white/5 p-3 rounded-lg">
                      <div className="flex items-center gap-2 text-gray-400 mb-1">
                        <MousePointer className="w-4 h-4" />
                        <span className="text-sm">Clicks</span>
                      </div>
                      <span className="text-2xl font-bold text-white">{project.clickCount}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      {project.subscriptionStatus === 'active' && (
                        <>
                          <button
                            onClick={() => setSelectedProjectForBanner(project)}
                            className="flex-1 px-4 py-2 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                          >
                            <Upload className="w-4 h-4" />
                            Upload Banner
                          </button>
                          <button
                            onClick={() => setSelectedProjectForProfile(project)}
                            className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-colors flex items-center justify-center gap-2"
                          >
                            Edit Profile
                          </button>
                        </>
                      )}
                      {project.subscriptionStatus === 'pending' && (
                        <button className="flex-1 px-4 py-2 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white rounded-lg font-medium hover:opacity-90 transition-opacity">
                          Complete Payment
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteProject(project._id)}
                        className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                    {project.subscriptionStatus === 'active' && (
                      <button
                        onClick={() => {
                          setSelectedProjectForFeatured(project)
                          setShowFeaturedModal(true)
                        }}
                        className="w-full px-4 py-2 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                      >
                        <Star className="w-4 h-4" />
                        {project.isFeatured && project.featuredUntil && new Date(project.featuredUntil) > new Date()
                          ? 'Extend Featured Listing'
                          : 'Feature Your Project'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0d1339] border border-white/10 rounded-2xl max-w-2xl w-full p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">Create New Project</h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Project Name */}
              <div>
                <label className="block text-gray-300 mb-2">Project Name</label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="e.g., CryptoVerse NFT"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
                />
              </div>

              {/* Website URL */}
              <div>
                <label className="block text-gray-300 mb-2">Website URL</label>
                <input
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://yourproject.com"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
                />
              </div>

              {/* Banner Upload */}
              <div>
                <label className="block text-gray-300 mb-2">Banner Image (1200x400px recommended)</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                {previewUrl ? (
                  <div className="relative">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg border border-white/10"
                    />
                    <button
                      onClick={() => {
                        setSelectedFile(null)
                        setPreviewUrl('')
                      }}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-48 border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center gap-3 hover:border-[#0052FF] transition-colors"
                  >
                    <Upload className="w-10 h-10 text-gray-500" />
                    <span className="text-gray-400">Click to upload banner image</span>
                    <span className="text-sm text-gray-500">Max size: 5MB</span>
                  </button>
                )}
              </div>

              {/* Payment Info */}
              <div className="bg-[#0052FF]/10 border border-[#0052FF]/30 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-5 h-5 text-[#60A5FA]" />
                  <div>
                    <p className="text-white font-medium">$20/month subscription</p>
                    <p className="text-sm text-gray-400">You'll be redirected to Stripe to complete payment</p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleStartPayment}
                disabled={processingPayment || !projectName || !websiteUrl}
                className="w-full px-6 py-3 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white rounded-lg font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {processingPayment ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    Continue to Payment ($20/month)
                  </>
                )}
              </button>
              
              <p className="text-xs text-gray-500 text-center mt-2">
                Banner upload will be available after payment confirmation
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Banner Upload Modal */}
      {selectedProjectForBanner && (
        <BannerUploadModal
          project={selectedProjectForBanner}
          onClose={() => {
            setSelectedProjectForBanner(null)
            if (user) {
              fetchUserProjects(user.userId)
            }
          }}
        />
      )}

      {/* Profile Edit Modal */}
      {selectedProjectForProfile && (
        <ProfileEditModal
          project={selectedProjectForProfile}
          onClose={() => {
            setSelectedProjectForProfile(null)
            if (user) {
              fetchUserProjects(user.userId)
            }
          }}
        />
      )}

      {/* Featured Listing Tier Selection Modal */}
      {showFeaturedModal && selectedProjectForFeatured && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0d1339] border border-white/10 rounded-2xl max-w-4xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-2xl font-bold text-white">Feature Your Project</h3>
                <p className="text-gray-400 mt-1">Boost visibility for {selectedProjectForFeatured.projectName}</p>
              </div>
              <button
                onClick={() => {
                  setShowFeaturedModal(false)
                  setSelectedProjectForFeatured(null)
                  setSelectedTier(null)
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tier Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Starter Tier */}
              <div
                onClick={() => setSelectedTier('starter')}
                className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                  selectedTier === 'starter'
                    ? 'border-[#60A5FA] bg-[#0052FF]/10'
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-xl font-bold text-white">Starter</h4>
                    <p className="text-gray-400 text-sm">3 Days Featured</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-white">$15</div>
                    <div className="text-sm text-gray-400">one-time</div>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#FFD700]" />
                    Featured badge for 3 days
                  </li>
                  <li className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#FFD700]" />
                    Priority placement
                  </li>
                </ul>
              </div>

              {/* Plus Tier */}
              <div
                onClick={() => setSelectedTier('plus')}
                className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                  selectedTier === 'plus'
                    ? 'border-[#60A5FA] bg-[#0052FF]/10'
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-xl font-bold text-white">Plus</h4>
                    <p className="text-gray-400 text-sm">1 Week Featured</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-white">$25</div>
                    <div className="text-sm text-gray-400">one-time</div>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#FFD700]" />
                    Featured badge for 7 days
                  </li>
                  <li className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#FFD700]" />
                    Priority placement
                  </li>
                  <li className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#FFD700]" />
                    Save 16% vs daily rate
                  </li>
                </ul>
              </div>

              {/* Pro Tier */}
              <div
                onClick={() => setSelectedTier('pro')}
                className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                  selectedTier === 'pro'
                    ? 'border-[#60A5FA] bg-[#0052FF]/10'
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-xl font-bold text-white">Pro</h4>
                    <p className="text-gray-400 text-sm">2 Weeks Featured</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-white">$45</div>
                    <div className="text-sm text-gray-400">one-time</div>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#FFD700]" />
                    Featured badge for 14 days
                  </li>
                  <li className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#FFD700]" />
                    Priority placement
                  </li>
                  <li className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#FFD700]" />
                    Save 32% vs daily rate
                  </li>
                </ul>
              </div>

              {/* Diamond Tier */}
              <div
                onClick={() => setSelectedTier('diamond')}
                className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                  selectedTier === 'diamond'
                    ? 'border-[#FFD700] bg-[#FFD700]/10'
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="px-3 py-1 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black text-xs font-bold rounded-full">
                    BEST VALUE
                  </span>
                </div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-xl font-bold text-white">Diamond</h4>
                    <p className="text-gray-400 text-sm">1 Month Featured</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-white">$80</div>
                    <div className="text-sm text-gray-400">one-time</div>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#FFD700]" />
                    Featured badge for 30 days
                  </li>
                  <li className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#FFD700]" />
                    Priority placement
                  </li>
                  <li className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#FFD700]" />
                    Save 47% vs daily rate
                  </li>
                  <li className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#FFD700]" />
                    Maximum visibility
                  </li>
                </ul>
              </div>
            </div>

            {/* Purchase Button */}
            <button
              onClick={() => selectedTier && handlePurchaseFeatured(selectedTier)}
              disabled={!selectedTier || processingFeatured}
              className="w-full px-6 py-3 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black rounded-lg font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {processingFeatured ? (
                <>
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Star className="w-5 h-5" />
                  {selectedTier ? `Purchase ${selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)} Tier` : 'Select a tier'}
                </>
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* Note: Footer is now rendered in App.tsx for all pages */}
    </div>
  )
}
